<template>
  <aside id="result-card">
    <div class="card">
      <div class="card-header">
        {{ isCurrentPlan ? $t('dash.plan.card.header.active') : $t('dash.plan.card.header.selection') }}
      </div>
      <div class="card-body">
        <div class="plan-header">
          <h2>{{ result.plan.id === 'custom' ? $t('dash.plan.card.custom.name') : result.plan.name }}</h2>
          <div class="offer-container">
            <span v-if="result.savingsBadge" class="savings-badge">
              {{ $t(result.offerText) }}
            </span>
            <span v-if="result.offerText" class="offer-text">
              {{ $t(result.savingsBadge, { amount: result.savingsAmount }) }}
            </span>
          </div>
        </div>

        <div class="price-display" v-if="result.plan.id !== 'custom'">
          <span class="price">{{ formattedPrice }}€ <sup>{{ $t('dash.plan.card.tax_label') }}</sup></span>
          <span class="period">&nbsp;{{ result.priceLabel ? $t(result.priceLabel) : '' }}</span>
        </div>

        <div class="contact-display" v-else>
          <span class="contact-text">
            {{ $t('dash.plan.card.custom.question') }}
          </span>
          <span class="contact-text">
            {{ $t('dash.plan.card.custom.answer') }}
          </span>
        </div>

        <ul class="features-list" v-if="result.plan.id !== 'custom'">
          <li>
            {{ $t('dash.plan.card.features.up_to') }}
            <span class="feature-value">{{
              result.displayValues.socialAccounts
            }}</span>
            {{ $t('dash.plan.card.features.social_accounts') }}
            <span v-if="result.extraCosts.social" class="extra-cost-text">
              {{ result.extraCosts.social }}
            </span>
          </li>
          <li>
            {{ $t('dash.plan.card.features.up_to') }}
            <span class="feature-value">{{ result.displayValues.users }}</span>
            {{ $t('dash.plan.card.features.users') }}
            <span v-if="result.extraCosts.users" class="extra-cost-text">
              {{ result.extraCosts.users }}
            </span>
          </li>
          <li>
            {{ $t('dash.plan.card.features.post_from') }}
            <span class="feature-value">{{
              result.displayValues.sources
            }}</span>
            {{ $t('dash.plan.card.features.sources') }}
            <span v-if="result.extraCosts.sources" class="extra-cost-text">
              {{ result.extraCosts.sources }}
            </span>
          </li>
          <li
            v-for="(featureKey, index) in featureKeys"
            :key="index"
            :class="{ 'not-included': !result.features[featureKey] }"
          >
            {{ $t(featureLabels[featureKey]) }}
          </li>
        </ul>

        <button 
          class="cta-btn" 
          :disabled="isCurrentPlan && result.plan.id !== 'custom'"
          :class="{ 'disabled': isCurrentPlan && result.plan.id !== 'custom' }"
          @click="handleCTAClick"
        >
          <template v-if="result.plan.id === 'custom'">
            {{ $t('dash.plan.card.button.contact') }}
          </template>
          <template v-else-if="isCurrentPlan">
            {{ $t('dash.plan.card.button.subscribed') }}
          </template>
          <template v-else>
            {{ $t('dash.plan.card.button.choose') }}
          </template>
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import { ENVIRONMENTS } from '../config/pricingConfig'
import { featureLabels } from '../data/plans'

export default {
  name: 'ResultCard',

  props: {
    result: {
      type: Object,
      required: true,
      validator: value => {
        return (
          value &&
          typeof value === 'object' &&
          value.plan &&
          typeof value.finalPrice === 'number' &&
          typeof value.priceLabel === 'string' &&
          value.displayValues &&
          typeof value.features === 'object'
        )
      }
    },
    environment: {
      type: String,
      required: true,
      validator: value => Object.values(ENVIRONMENTS).includes(value)
    },
    isCurrentPlan: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ENVIRONMENTS,
      featureLabels,
      featureKeys: [
        'native',
        'planification',
        'moderation',
        'reporting',
        'collaboration'
      ]
    }
  },

  computed: {
    formattedPrice() {
      return this.result.finalPrice.toLocaleString('fr-FR')
    }
  },

  methods: {
    // Handle CTA button click (emit events for parent to handle)
    handleCTAClick() {
      if (this.result.plan.id === 'custom') {
        this.$emit('contact')
      } else {
        this.$emit('subscribe')
      }
    },

    // Redirect to SaaS with plan ID + period as query parameter (legacy, not used in dashboard)
    redirectToSaaS() {
      const planId = this.result.plan.id
      const period = this.result.period
      const saasUrl = `https://saas.mashup-web.com/#/?plan=${planId}&period=${period}`
      window.location.href = saasUrl
    },

    // Open chat based on environment (HubSpot for Webflow, Crisp for SaaS) (legacy, not used in dashboard)
    openChat() {
      const customMessage = this.$t('dash.plan.card.custom.chat_message')

      switch (this.environment) {
        case ENVIRONMENTS.WEBFLOW:
          if (
            window.HubSpotConversations &&
            window.HubSpotConversations.widget
          ) {
            window.HubSpotConversations.widget.open()
            // Pre-fill HubSpot chat message
            setTimeout(() => {
              const iframe = document.querySelector(
                '#hubspot-messages-iframe-container iframe'
              )
              if (iframe && iframe.contentDocument) {
                const messageField = iframe.contentDocument.querySelector(
                  '[data-test-id="widget-textarea"]'
                )
                if (messageField) {
                  messageField.value = customMessage
                  messageField.dispatchEvent(
                    new Event('input', { bubbles: true })
                  )
                  // Trigger focus to show the message
                  messageField.focus()
                }
              }
            }, 800)
          } else {
            console.warn('HubSpot chat not available')
          }
          break

        case ENVIRONMENTS.SAAS:
          if (window.$crisp) {
            window.$crisp.push(['do', 'chat:open'])
            // Pre-fill Crisp chat message
            window.$crisp.push(['set', 'message:text', [customMessage]])
          } else {
            console.warn('Crisp chat not available')
          }
          break

        default:
          console.error(`Unknown environment: ${this.environment}`)
      }
    }
  }
}
</script>

<style>
/* Styles to /assets/scss/components/pricing.scss */
</style>
